/**
 * Shared UI component for UI Autocomplete Field.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';

import { UiAutocompleteOption } from './ui-autocomplete-field.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { resolveTranslatedUiLabel } from '../ui-label-translate.util';

interface UiPrimeAutocompleteOption extends UiAutocompleteOption {
  translatedCaption: string;
  translatedLabel: string;
}

@Component({
  selector: 'ui-autocomplete-field',
  standalone: true,
  imports: [AutoCompleteModule, FormsModule, UiLabelTranslatePipe],
  templateUrl: './ui-autocomplete-field.component.html',
  styleUrl: './ui-autocomplete-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAutocompleteFieldComponent {
  private readonly translate = inject(TranslateService);
  private readonly languageVersion = signal(0);

  readonly label = input('');
  readonly value = input('');
  readonly placeholder = input('');
  readonly required = input(false);
  readonly readonly = input(false);
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly error = input('');
  readonly hint = input('');
  readonly emptyMessage = input('No matches found.');
  readonly minSearchLength = input(3);
  readonly showEmptyState = input(true);
  readonly options = input<UiAutocompleteOption[]>([]);
  readonly valueChange = output<string>();
  readonly optionSelected = output<string>();
  readonly resolvedOptions = computed<UiPrimeAutocompleteOption[]>(() => {
    this.languageVersion();

    return this.options().map((option) => ({
      ...option,
      translatedCaption: option.caption ? this.resolveLabel(option.caption) : '',
      translatedLabel: this.resolveLabel(option.label)
    }));
  });

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      this.languageVersion.update((version) => version + 1);
    });
  }

  onValueChange(value: string | UiPrimeAutocompleteOption | null | undefined): void {
    if (typeof value === 'string') {
      this.valueChange.emit(value);
    }
  }

  onSelect(event: AutoCompleteSelectEvent): void {
    const option = event.value as UiPrimeAutocompleteOption | undefined;

    if (option?.value) {
      this.optionSelected.emit(option.value);
    }
  }

  private resolveLabel(value: string): string {
    return resolveTranslatedUiLabel(this.translate, value);
  }
}
